import { LabelMini, LabelMiniStrong } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { ReactNode } from 'react';

import { BidConsoleOpener } from '@/components/BidConsoleOpener/BidConsoleOpener';
import { RouteLink } from '@/components/RouteLinks/RouteLinks';
import { AuctionCardClickedTrigger } from '@/utils/amplitude/analytics/catalog/trackAuctionCardClicked.analytics';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

import { CoverImage, CoverImageTile } from './CoverImageTile';

type Props = {
    catalogId: number;
    catalogUrl: string;
    children?: ReactNode;
    coverImages: CoverImage[];
    lazyLoadImages?: boolean;
    live: boolean;
    moreLotsCount: number;
    rel: string;
    shouldShowBidNowCta: boolean;
};

export const HomepageAuctionCardCollage = ({
    catalogId,
    catalogUrl,
    children,
    coverImages,
    lazyLoadImages,
    live,
    moreLotsCount,
    rel,
    shouldShowBidNowCta,
}: Props) => {
    const formatMessage = useAppFormatMessage();

    const bidNowLabel = (
        <LabelMiniStrong
            className="max-w-full cursor-pointer truncate text-center hover:underline hover:decoration-current"
            color="critical"
            data-testid="homepage-auction-card-bid-now"
            data-track-trigger={AuctionCardClickedTrigger.BidNowClick}
        >
            {formatMessage(['live_auction'])}
        </LabelMiniStrong>
    );

    const lotCta = shouldShowBidNowCta ? (
        live ? (
            <BidConsoleOpener catalogId={catalogId}>{bidNowLabel}</BidConsoleOpener>
        ) : (
            <RouteLink
                className="inline-flex max-w-full cursor-pointer truncate text-center font-semibold uppercase text-text-critical no-underline hover:text-text-critical hover:underline hover:decoration-current"
                data-testid="homepage-auction-card-bid-now"
                data-track-trigger={AuctionCardClickedTrigger.BidNowClick}
                rel={rel}
                size="xs"
                to={catalogUrl}
            >
                {formatMessage(['live_auction'])}
            </RouteLink>
        )
    ) : (
        <RouteLink
            className="max-w-full truncate text-center font-semibold uppercase no-underline hover:underline"
            data-testid="homepage-auction-card-browse-lots"
            data-track-trigger={AuctionCardClickedTrigger.ImageClick}
            rel={rel}
            size="xs"
            to={catalogUrl}
        >
            {formatMessage(['catalog_card', 'homepage', 'browseLots'])}
        </RouteLink>
    );

    return (
        <div
            className="relative flex aspect-square w-full flex-col border-b border-border-secondary"
            data-track-trigger={AuctionCardClickedTrigger.ImageClick}
        >
            <div className="flex h-1/2 w-full">
                {coverImages.slice(0, 2).map((image, index) => (
                    <CoverImageTile
                        image={image}
                        key={`${catalogId}-cover-${index}`}
                        lazyLoadImages={lazyLoadImages}
                        rel={rel}
                        to={catalogUrl}
                    />
                ))}
            </div>
            <div className="flex h-1/2 w-full">
                <CoverImageTile
                    image={coverImages[2]}
                    lazyLoadImages={lazyLoadImages}
                    rel={rel}
                    to={catalogUrl}
                />
                <div
                    className={cn(
                        'flex h-full w-1/2 flex-col items-center justify-center gap-sm overflow-hidden p-xs',
                        shouldShowBidNowCta ? 'bg-background-critical-light' : 'bg-background-secondary'
                    )}
                    data-testid="homepage-auction-card-more-lots-tile"
                >
                    <LabelMini
                        className="max-w-full truncate text-center uppercase"
                        color="primary"
                    >
                        {formatMessage(['catalog_card', 'homepage', 'moreLots'], {
                            values: { count: moreLotsCount },
                        })}
                    </LabelMini>
                    {lotCta}
                </div>
            </div>
            {children}
        </div>
    );
};
